import { FeatureCollection, Point, Polygon } from 'geojson';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Layer, Source, Map } from 'shared/map-exports';
import { useParams } from 'react-router-dom';
import { useAuthState } from 'state/useAuthState';
import { useAlertCameraPlayerState } from 'features/cameras/hooks/useAlertCameraPlayerState';
import { useAlertCameraPlayerMouseOverPositionValue } from 'features/cameras/hooks/useAlertCameraPlayerMouseOverPosition';
import {
  useAlertCamera,
  useAlertCameras,
} from 'features/cameras/hooks/useAlertCameras';
import { addVisible } from 'shared/addVisible';
import { useMapZoomLevel } from 'hooks/useMapZoomLevel';
import { MapboxFeature, MapClickHandler } from 'features/mapping/mapping.types';
import { useMapLayerEvents } from 'features/mapping/hooks/useMapLayerEvents';
import { useCenterMap } from 'features/mapping/hooks/useCenterMap';
import { history } from 'shared/history';
import { OVERLAY_ICONS_BEFORE_ID } from 'features/mapping/styles';
import {
  CameraGeoJson,
  GenericCameraFeatureProperties,
} from './AlertCamerasLayer.types';
import {
  generateAzLineGeojson,
  generateAzimuthLineMouseOverGeojson,
  generateRecentlyMovedCamerasViewshedGeojson,
  generateViewshedGeojson,
  getAllCameraLabelsGeoJson,
  getCameraFromGroup,
  getCameraIdFromFeature,
  hasMultiDirectionFaaCameras,
  recentlyMovedCamerasToGeojson,
  toGeojsonFeatures,
} from './AlertCamerasLayer.utils';
import {
  PTZ_SOURCE_ID,
  PTZ_ARROWS_SOURCE_ID,
  STATIONARY_SOURCE_ID,
  PTZ_ICONS_LAYER_ID,
  ALL_LABELS_SOURCE_ID,
  ALL_LABELS_LAYER_ID,
  PTZ_ARROW_ICONS_LAYER_ID,
  STATIONARY_ICONS_LAYER_ID,
  PTZ_VIEWSHED_LAYER_FILL_ID,
  PTZ_VIEWSHED_LAYER_STROKE_ID,
  STATIONARY_VIEWSHED_SOURCE_ID,
  PTZ_VIEWSHED_SOURCE_ID,
  STATIONARY_VIEWSHED_LAYER_FILL_ID,
  STATIONARY_VIEWSHED_LAYER_STROKE_ID,
  VIEWSHED_FILL_OPACITY,
  VIEWSHED_STROKE_OPACITY,
  VIEWSHED_FILL_OPACITY_SELECTED,
  VIEWSHED_STROKE_OPACITY_SELECTED,
  AZ_LINE_SOURCE_ID,
  AZ_LINE_LAYER_STROKE_ID,
  AZ_LINE_MOUSE_OVER_SOURCE_ID,
  AZ_LINE_MOUSE_OVER_LAYER_STROKE_ID,
  MIN_ZOOM_VISIBILITY_RECENTLY_MOVED,
  MIN_ZOOM_VISIBILITY_PTZ,
  RECENTLY_MOVED_PTZ_ICONS_LAYER_ID,
  RECENTLY_MOVED_PTZ_ARROW_ICONS_LAYER_ID,
  RECENTLY_MOVED_PTZ_SOURCE_ID,
  RECENTLY_MOVED_PTZ_ARROWS_SOURCE_ID,
  RECENTLY_MOVED_PTZ_VIEWSHED_SOURCE_ID,
  RECENTLY_MOVED_PTZ_VIEWSHED_LAYER_FILL_ID,
  RECENTLY_MOVED_PTZ_VIEWSHED_LAYER_STROKE_ID,
} from './AlertCamerasLayer.constants';
import {
  getArrowIconStyles,
  getAzimuthLineMouseOverStyles,
  getAzimuthLineStyles,
  getBaseIconStyles,
  getIconImageLayout,
  getPaintOpacityForActiveId,
  getViewshedFillStyles,
  getViewshedStrokeStyles,
  labelStyles,
} from './AlertCamerasLayer.styles';

type CamerasLayerProps = {
  visible: boolean;
  interactive?: boolean;
  withLabels?: boolean;
  drawerIsOpen?: boolean;
  selectedCameraId?: string;
};

export const resolveCameraPath = (
  geoJsonFeatures: MapboxFeature<GenericCameraFeatureProperties>[],
): string | null => {
  const featureProperties = geoJsonFeatures[0].properties;
  const cameraId = getCameraIdFromFeature(featureProperties);
  if (!cameraId) return null;
  return `/camera/${cameraId}`;
};

export const alertCamerasClickHandler: MapClickHandler<
  GenericCameraFeatureProperties
> = (geoJsonFeatures): void => {
  const path = resolveCameraPath(geoJsonFeatures);
  if (path) history.push(path);
};

export const AlertCamerasLayer = (props: CamerasLayerProps): JSX.Element => {
  const {
    visible,
    interactive = true,
    withLabels = false,
    drawerIsOpen = true,
    selectedCameraId,
  } = props;
  const { alertCameraGroups = [], alertCameras = [] } = useAlertCameras({
    enabled: visible,
    withRealtimeUpdates: true,
  });
  const { timelapseFrame, playerStatus } = useAlertCameraPlayerState();
  const params = useParams<{ cameraId?: string }>();
  const {
    permissions: { isInternalUser },
  } = useAuthState();
  const [hoveredCameraId, setHoveredCameraId] = useState<string | null>(null);
  const { mouseX, isHovering } = useAlertCameraPlayerMouseOverPositionValue();

  const obfuscatedCameraId = params.cameraId || selectedCameraId;
  // We use the `alertCamera` query to retrieve the camera.id from the backend,
  // since we need to support legacy camera ids that may not match the compositeId
  // used in new GIS API. Once we have the camera.id, we can find the corresponding
  // camera in the `alertCameras` list, which provides the lat/lng for centering the
  // map and ensures that map layer styles update correctly based on the active camera id.
  const { alertCamera } = useAlertCamera({ id: obfuscatedCameraId });
  const activeCameraId = alertCamera?.id || obfuscatedCameraId;

  // On mobile, tapping a camera also fires the hover event, which sets
  // `hoveredCameraId`. On desktop, the camera viewshed should show a newly-hovered
  // camera's viewshed even if the timelapse player is playing, so the `hoveredCameraId`
  // takes precendence in viewshed display.
  //
  // To avoid `hoveredCameraId` persisting and showing the "live" viewshed of a camera
  // after timelapse playback has initiated, reset it whenever the player status changes
  // to "playing".
  useEffect(() => {
    if (playerStatus === 'playingTimelapse') {
      setHoveredCameraId(null);
    }
  }, [playerStatus]);

  const handleHover = useCallback(
    (features: MapboxFeature<GenericCameraFeatureProperties>[], map: Map) => {
      const { properties } = features[0];
      if (activeCameraId === properties.id) {
        return;
      }

      const currentCameraGroupHover = alertCameraGroups.find((cameraGroup) =>
        getCameraFromGroup(cameraGroup, properties.id!),
      );
      currentCameraGroupHover && setHoveredCameraId(currentCameraGroupHover.id);

      const selectedCameraGroupIds =
        'cameraGroupId' in properties
          ? [properties.id, properties.cameraGroupId]
          : [properties.id];
      const selectedChildCameraIds =
        'childCameraIds' in properties
          ? JSON.parse(properties.childCameraIds as unknown as string)
          : [];

      const selectedIds = [
        ...selectedCameraGroupIds,
        ...selectedChildCameraIds,
      ];

      [
        PTZ_ICONS_LAYER_ID,
        PTZ_ARROW_ICONS_LAYER_ID,
        RECENTLY_MOVED_PTZ_ICONS_LAYER_ID,
        RECENTLY_MOVED_PTZ_ARROW_ICONS_LAYER_ID,
        STATIONARY_ICONS_LAYER_ID,
      ].forEach((layerId) => {
        map.setLayoutProperty(layerId, 'icon-image', [
          'case',
          ['in', ['get', 'id'], ['literal', selectedIds]],
          ['get', 'iconFocused'],
          ['get', 'icon'],
        ]);
      });

      [
        STATIONARY_VIEWSHED_LAYER_FILL_ID,
        PTZ_VIEWSHED_LAYER_FILL_ID,
        RECENTLY_MOVED_PTZ_VIEWSHED_LAYER_FILL_ID,
      ].forEach((layerId) => {
        map.setPaintProperty(layerId, 'fill-opacity', [
          'case',
          ['in', ['get', 'id'], ['literal', selectedIds]],
          VIEWSHED_FILL_OPACITY_SELECTED,
          VIEWSHED_FILL_OPACITY,
        ]);
      });

      [
        STATIONARY_VIEWSHED_LAYER_STROKE_ID,
        PTZ_VIEWSHED_LAYER_STROKE_ID,
        RECENTLY_MOVED_PTZ_VIEWSHED_LAYER_STROKE_ID,
      ].forEach((layerId) => {
        map.setPaintProperty(layerId, 'line-opacity', [
          'case',
          ['in', ['get', 'id'], ['literal', selectedIds]],
          VIEWSHED_STROKE_OPACITY_SELECTED,
          VIEWSHED_STROKE_OPACITY,
        ]);
      });
    },
    [activeCameraId, alertCameraGroups],
  );

  const handleHoverOff = useCallback(
    (map: Map) => {
      setHoveredCameraId(null);

      [
        PTZ_ICONS_LAYER_ID,
        PTZ_ARROW_ICONS_LAYER_ID,
        RECENTLY_MOVED_PTZ_ICONS_LAYER_ID,
        RECENTLY_MOVED_PTZ_ARROW_ICONS_LAYER_ID,
        STATIONARY_ICONS_LAYER_ID,
      ].forEach((layerId) => {
        map.setLayoutProperty(
          layerId,
          'icon-image',
          getIconImageLayout(activeCameraId),
        );
      });

      [
        STATIONARY_VIEWSHED_LAYER_FILL_ID,
        PTZ_VIEWSHED_LAYER_FILL_ID,
        RECENTLY_MOVED_PTZ_VIEWSHED_LAYER_FILL_ID,
      ].forEach((layerId) => {
        map.setPaintProperty(
          layerId,
          'fill-opacity',
          getPaintOpacityForActiveId(activeCameraId),
        );
      });

      [
        STATIONARY_VIEWSHED_LAYER_STROKE_ID,
        PTZ_VIEWSHED_LAYER_STROKE_ID,
        RECENTLY_MOVED_PTZ_VIEWSHED_LAYER_STROKE_ID,
      ].forEach((layerId) => {
        map.setPaintProperty(
          layerId,
          'line-opacity',
          getPaintOpacityForActiveId(activeCameraId),
        );
      });
    },
    [activeCameraId],
  );

  useMapLayerEvents<GenericCameraFeatureProperties>({
    layerId: PTZ_ICONS_LAYER_ID,
    enabled: visible,
    onHover: interactive ? handleHover : undefined,
    onHoverOff: interactive ? handleHoverOff : undefined,
    stopTouchMouseEventPropagation: true,
  });
  useMapLayerEvents<GenericCameraFeatureProperties>({
    layerId: PTZ_ARROW_ICONS_LAYER_ID,
    enabled: visible,
    onHover: interactive ? handleHover : undefined,
    onHoverOff: interactive ? handleHoverOff : undefined,
    stopTouchMouseEventPropagation: true,
  });
  useMapLayerEvents<GenericCameraFeatureProperties>({
    layerId: RECENTLY_MOVED_PTZ_ICONS_LAYER_ID,
    enabled: visible,
    onHover: interactive ? handleHover : undefined,
    onHoverOff: interactive ? handleHoverOff : undefined,
    stopTouchMouseEventPropagation: true,
  });
  useMapLayerEvents<GenericCameraFeatureProperties>({
    layerId: RECENTLY_MOVED_PTZ_ARROW_ICONS_LAYER_ID,
    enabled: visible,
    onHover: interactive ? handleHover : undefined,
    onHoverOff: interactive ? handleHoverOff : undefined,
    stopTouchMouseEventPropagation: true,
  });
  useMapLayerEvents<GenericCameraFeatureProperties>({
    layerId: STATIONARY_ICONS_LAYER_ID,
    enabled: visible,
    onHover: interactive ? handleHover : undefined,
    onHoverOff: interactive ? handleHoverOff : undefined,
    stopTouchMouseEventPropagation: true,
  });

  // do not modify the geojson based on selectedState or it will have to re-render.
  const geojsonPtz = useMemo(() => {
    const baseIcons: CameraGeoJson[] = [];
    const arrowIcons: CameraGeoJson[] = [];
    alertCameraGroups
      .filter((camera) => camera.hasPtz || hasMultiDirectionFaaCameras(camera))
      .forEach((camera) => {
        const features = toGeojsonFeatures(camera);
        baseIcons.push(...features.baseIcons);
        arrowIcons.push(...features.arrowIcons);
      });
    return {
      baseIcons: {
        type: 'FeatureCollection',
        features: baseIcons,
      },
      arrowIcons: {
        type: 'FeatureCollection',
        features: arrowIcons,
      },
    };
  }, [alertCameraGroups]);

  const geojsonStationary = useMemo(
    () => ({
      type: 'FeatureCollection',
      features: alertCameraGroups
        .filter(
          (cameraGroup) =>
            !cameraGroup.hasPtz && !hasMultiDirectionFaaCameras(cameraGroup),
        )
        .flatMap((cameraGroup) => toGeojsonFeatures(cameraGroup).baseIcons),
    }),
    [alertCameraGroups],
  );

  // This geojson will be built after clicking or hovering on a camera
  const geojsonViewshedStationary: FeatureCollection<
    Point | Polygon,
    GenericCameraFeatureProperties
  > = useMemo(() => {
    return generateViewshedGeojson({
      alertCameraGroups,
      filterFn: (cameraGroup) =>
        !cameraGroup.hasPtz && !hasMultiDirectionFaaCameras(cameraGroup),
      activeCameraId,
      hoveredCameraId,
      currentTimelapseFrame: timelapseFrame,
    });
  }, [hoveredCameraId, activeCameraId, alertCameraGroups, timelapseFrame]);

  const geojsonViewshedPtz: FeatureCollection<
    Point | Polygon,
    GenericCameraFeatureProperties
  > = useMemo(() => {
    return generateViewshedGeojson({
      alertCameraGroups,
      filterFn: (cameraGroup) =>
        cameraGroup.hasPtz || hasMultiDirectionFaaCameras(cameraGroup),
      activeCameraId,
      hoveredCameraId,
      currentTimelapseFrame: timelapseFrame,
    });
  }, [activeCameraId, alertCameraGroups, hoveredCameraId, timelapseFrame]);

  const activeCameraLatLng = useMemo(() => {
    if (!activeCameraId) {
      return null;
    }

    const activeCamera = alertCameras.find(
      (camera) => camera.id === activeCameraId,
    );
    if (!activeCamera) {
      return null;
    }

    return activeCamera?.latlng;
  }, [activeCameraId, alertCameras]);

  useCenterMap({
    latLng: activeCameraLatLng,
    drawerIsOpen,
    defaultSnapPointPct: 0.7,
    zoomIn: true,
  });

  const allLabelsGeoJson = useMemo(
    () => getAllCameraLabelsGeoJson(geojsonPtz.baseIcons.features),
    [geojsonPtz.baseIcons.features],
  );

  const zoomLevel = useMapZoomLevel();

  const geojsonAzLine = useMemo(
    () => generateAzLineGeojson(alertCameras, zoomLevel),
    [alertCameras, zoomLevel],
  );

  const geojsonAzLineMouseOver = useMemo(
    () =>
      generateAzimuthLineMouseOverGeojson(
        mouseX,
        alertCameras,
        activeCameraId,
        timelapseFrame,
      ),
    [mouseX, alertCameras, activeCameraId, timelapseFrame],
  );

  const recentlyMovedCamerasGeoJson = useMemo(() => {
    return recentlyMovedCamerasToGeojson(alertCameras);
  }, [alertCameras]);

  const recentlyMovedCamerasViewshedGeojson = useMemo(() => {
    return generateRecentlyMovedCamerasViewshedGeojson({
      alertCameras,
      activeCameraId,
      hoveredCameraId,
      currentTimelapseFrame: timelapseFrame,
    });
  }, [activeCameraId, alertCameras, hoveredCameraId, timelapseFrame]);

  return (
    <>
      <Source
        id={AZ_LINE_MOUSE_OVER_SOURCE_ID}
        type="geojson"
        data={geojsonAzLineMouseOver}
      >
        <Layer
          beforeId={OVERLAY_ICONS_BEFORE_ID}
          id={AZ_LINE_MOUSE_OVER_LAYER_STROKE_ID}
          {...addVisible(getAzimuthLineMouseOverStyles(isHovering), visible)}
        />
      </Source>
      <Source id={AZ_LINE_SOURCE_ID} type="geojson" data={geojsonAzLine}>
        <Layer
          beforeId={OVERLAY_ICONS_BEFORE_ID}
          id={AZ_LINE_LAYER_STROKE_ID}
          {...addVisible(getAzimuthLineStyles(isInternalUser), visible)}
        />
      </Source>
      <Source id={STATIONARY_SOURCE_ID} type="geojson" data={geojsonStationary}>
        <Layer
          beforeId={OVERLAY_ICONS_BEFORE_ID}
          id={STATIONARY_ICONS_LAYER_ID}
          {...addVisible(
            getBaseIconStyles({
              cameraType: 'stationary',
              isInternalUser,
              activeCameraId,
            }),
            visible,
          )}
          metadata={{ interactive }}
        />
      </Source>
      <Source id={PTZ_SOURCE_ID} type="geojson" data={geojsonPtz.baseIcons}>
        <Layer
          beforeId={OVERLAY_ICONS_BEFORE_ID}
          id={PTZ_ICONS_LAYER_ID}
          {...addVisible(
            getBaseIconStyles({
              cameraType: 'ptz',
              isInternalUser,
              activeCameraId,
            }),
            visible,
          )}
          metadata={{ interactive }}
        />
      </Source>
      <Source
        id={RECENTLY_MOVED_PTZ_SOURCE_ID}
        type="geojson"
        data={recentlyMovedCamerasGeoJson.baseIcons}
      >
        <Layer
          beforeId={OVERLAY_ICONS_BEFORE_ID}
          id={RECENTLY_MOVED_PTZ_ICONS_LAYER_ID}
          {...addVisible(
            getBaseIconStyles({
              cameraType: 'ptz',
              isInternalUser,
              activeCameraId,
              minZoom: isInternalUser
                ? MIN_ZOOM_VISIBILITY_RECENTLY_MOVED
                : undefined,
              maxZoom: isInternalUser ? MIN_ZOOM_VISIBILITY_PTZ : undefined,
            }),
            visible,
          )}
          metadata={{ interactive }}
        />
      </Source>
      {withLabels && (
        <Source
          id={ALL_LABELS_SOURCE_ID}
          type="geojson"
          data={allLabelsGeoJson}
        >
          <Layer
            beforeId={OVERLAY_ICONS_BEFORE_ID}
            id={ALL_LABELS_LAYER_ID}
            {...addVisible(labelStyles, visible)}
          />
        </Source>
      )}
      <Source
        id={PTZ_ARROWS_SOURCE_ID}
        type="geojson"
        data={geojsonPtz.arrowIcons}
      >
        <Layer
          beforeId={OVERLAY_ICONS_BEFORE_ID}
          id={PTZ_ARROW_ICONS_LAYER_ID}
          {...addVisible(
            getArrowIconStyles({
              cameraType: 'ptz',
              isInternalUser,
              activeCameraId,
            }),
            visible,
          )}
          metadata={{ interactive }}
        />
      </Source>
      <Source
        id={RECENTLY_MOVED_PTZ_ARROWS_SOURCE_ID}
        type="geojson"
        data={recentlyMovedCamerasGeoJson.arrowIcons}
      >
        <Layer
          beforeId={OVERLAY_ICONS_BEFORE_ID}
          id={RECENTLY_MOVED_PTZ_ARROW_ICONS_LAYER_ID}
          {...addVisible(
            getArrowIconStyles({
              cameraType: 'ptz',
              isInternalUser,
              activeCameraId,
              minZoom: isInternalUser
                ? MIN_ZOOM_VISIBILITY_RECENTLY_MOVED
                : undefined,
              maxZoom: isInternalUser ? MIN_ZOOM_VISIBILITY_PTZ : undefined,
            }),
            visible,
          )}
          metadata={{ interactive }}
        />
      </Source>
      <Source
        id={STATIONARY_VIEWSHED_SOURCE_ID}
        type="geojson"
        data={geojsonViewshedStationary}
      >
        <Layer
          beforeId={OVERLAY_ICONS_BEFORE_ID}
          id={STATIONARY_VIEWSHED_LAYER_FILL_ID}
          {...addVisible(
            getViewshedFillStyles({
              cameraType: 'stationary',
              isInternalUser,
              activeCameraId,
            }),
            visible,
          )}
        />
        <Layer
          beforeId={OVERLAY_ICONS_BEFORE_ID}
          id={STATIONARY_VIEWSHED_LAYER_STROKE_ID}
          {...addVisible(
            getViewshedStrokeStyles({
              cameraType: 'stationary',
              isInternalUser,
              activeCameraId,
            }),
            visible,
          )}
        />
      </Source>
      <Source
        id={PTZ_VIEWSHED_SOURCE_ID}
        type="geojson"
        data={geojsonViewshedPtz}
      >
        <Layer
          beforeId={OVERLAY_ICONS_BEFORE_ID}
          id={PTZ_VIEWSHED_LAYER_FILL_ID}
          {...addVisible(
            getViewshedFillStyles({
              cameraType: 'ptz',
              isInternalUser,
              activeCameraId,
            }),
            visible,
          )}
        />
        <Layer
          beforeId={OVERLAY_ICONS_BEFORE_ID}
          id={PTZ_VIEWSHED_LAYER_STROKE_ID}
          {...addVisible(
            getViewshedStrokeStyles({
              cameraType: 'ptz',
              isInternalUser,
              activeCameraId,
            }),
            visible,
          )}
        />
      </Source>
      <Source
        id={RECENTLY_MOVED_PTZ_VIEWSHED_SOURCE_ID}
        type="geojson"
        data={recentlyMovedCamerasViewshedGeojson}
      >
        <Layer
          beforeId={OVERLAY_ICONS_BEFORE_ID}
          id={RECENTLY_MOVED_PTZ_VIEWSHED_LAYER_FILL_ID}
          {...addVisible(
            getViewshedFillStyles({
              cameraType: 'ptz',
              isInternalUser,
              activeCameraId,
              minZoom: MIN_ZOOM_VISIBILITY_RECENTLY_MOVED,
              maxZoom: MIN_ZOOM_VISIBILITY_PTZ,
            }),
            visible,
          )}
        />
        <Layer
          beforeId={OVERLAY_ICONS_BEFORE_ID}
          id={RECENTLY_MOVED_PTZ_VIEWSHED_LAYER_STROKE_ID}
          {...addVisible(
            getViewshedStrokeStyles({
              cameraType: 'ptz',
              isInternalUser,
              activeCameraId,
              minZoom: MIN_ZOOM_VISIBILITY_RECENTLY_MOVED,
              maxZoom: MIN_ZOOM_VISIBILITY_PTZ,
            }),
            visible,
          )}
        />
      </Source>
    </>
  );
};
